import React, { useEffect, useState, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './Blog.css';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import FaqStructuredData from './FaqStructuredData';
import { Helmet } from 'react-helmet';

const Blog = ({ url }) => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [blogFaqs, setBlogFaqs] = useState([]);
  const [blogTags, setBlogTags] = useState([]);
  const [hasFaqs, setHasFaqs] = useState(false);
  const [hasTags, setHasTags] = useState(false);
  const [currentUrl, setCurrentUrl] = useState('');
  const [activeSection, setActiveSection] = useState('introduction');

  // Create refs for scrolling to sections
  const introductionRef = useRef(null);
  const sectionRefs = useRef({});
  const faqRef = useRef(null);

  useEffect(() => {
    // Set current URL for sharing
    setCurrentUrl(window.location.href);

    // Scroll to top when page loads
    window.scrollTo(0, 0);

    const fetchBlogData = async () => {
      try {
        setLoading(true);
        // Get the blog post based on slug
        const res = await axios.get(`${url}/blogs/${slug}`);
        console.log(res.data);
        
        if (res.status === 200) {
          const blogData = res.data;

          setBlog(blogData.blog);
          // Check if blog has FAQs and set them
          if (blogData.faqs) {
            setHasFaqs(true);
            if (blogData.faqs && Array.isArray(blogData.faqs)) {
              setBlogFaqs(blogData.faqs);
            }
          }
          if (blogData.tags) {
            setHasTags(true);
            if (blogData.tags && Array.isArray(blogData.tags)) {
              setBlogTags(blogData.tags);
            }
          }
          // Also fetch related posts (same category)
          try {
            const response = await axios.get(`${url}/blogs`); // Fetch all blogs

            if (response.status === 200) {
              const allBlogs = response.data;
              const currentTitleWords = blogData['blog'].blog_name
                .toLowerCase()
                .split(/\s+/)
                .filter(word => word.length > 2); // Filter out short/common words

              const related = allBlogs.filter(post => {
                if (post.blog_slug === blogData['blog'].blog_slug) return false; // Exclude current blog

                const postTitle = post.blog_name?.toLowerCase() || '';
                return currentTitleWords.some(word => postTitle.includes(word));
              });

              setRelatedPosts(related.slice(0, 3)); // Show top 3
            }
          } catch (error) {
            console.error("Error fetching related blogs:", error);
          }
        }
      } catch (err) {
        console.error("Error fetching blog data:", err);
        setError("Failed to load blog post. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchBlogData();
    }
  }, [url, slug]);

  // Check URL for hash on page load and scroll to that section
  useEffect(() => {
    // This will handle direct links with hash fragments
    if (window.location.hash) {
      const id = window.location.hash.substring(1); // Remove the # character
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          setActiveSection(id);
        }
      }, 500); // Small delay to ensure content is rendered
    }
  }, [blog]); // Re-run when blog content loads

  // Intersection Observer for active section tracking
  useEffect(() => {
    if (!blog) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -75% 0px" }
    );

    // Observe introduction
    const introEl = document.getElementById('introduction');
    if (introEl) observer.observe(introEl);

    // Observe all tag sections
    if (hasTags && blogTags.length > 0) {
      blogTags.forEach((tag) => {
        const sectionId = getSafeId(tag.blog_tag);
        const sectionEl = document.getElementById(sectionId);
        if (sectionEl) observer.observe(sectionEl);
      });
    }

    // Observe FAQ section
    if (hasFaqs) {
      const faqEl = document.getElementById('faqs');
      if (faqEl) observer.observe(faqEl);
    }

    return () => {
      observer.disconnect();
    };
  }, [blog, hasTags, blogTags, hasFaqs]);

  // Reading progress indicator
  useEffect(() => {
    const updateReadingProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = scrollTop / docHeight * 100;

      const progressBar = document.getElementById('reading-progress');
      if (progressBar) {
        progressBar.style.width = `${scrollPercent}%`;
      }
    };

    window.addEventListener('scroll', updateReadingProgress);
    return () => window.removeEventListener('scroll', updateReadingProgress);
  }, []);

  // Function to format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Function to calculate reading time
  const calculateReadingTime = (content) => {
    if (!content) return "5 min read";

    // Strip HTML tags
    const text = content.replace(/<[^>]*>/g, '');

    // Count words (average reading speed is 200-250 words per minute)
    const words = text.split(/\s+/).length;
    const minutes = Math.ceil(words / 200);

    return `${minutes} min read`;
  };

  // Function to scroll to a section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);

      // Update URL without page reload
      window.history.pushState(null, '', `#${sectionId}`);
    }
  };

  // Function to toggle FAQ answers
  const toggleFaqAnswer = (index) => {
    const faqItem = document.getElementById(`faq-${index}`);
    if (faqItem) {
      faqItem.classList.toggle('active');
    }
  };

  // Function to generate safe IDs for sections
  const getSafeId = (text) => {
    return text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  const blogTitle = blog && blog.blog_name || 'Check out this article!';
  const blogDescription = blog && blog.blog_description || '';
  const readingTime = blog && calculateReadingTime(blog.blog_text);

  const handleShare = (platform, e) => {
    e.preventDefault();

    let shareUrl = '';
    const windowFeatures = 'width=580,height=296';

    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(blogTitle)}&url=${encodeURIComponent(currentUrl)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(blogTitle + ' - ' + currentUrl)}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, `${platform}-share`, windowFeatures);
  };

  const emailShareUrl = `mailto:?subject=${encodeURIComponent(blogTitle)}&body=${encodeURIComponent('Check out this blog: ' + blogDescription + ' - ' + currentUrl)}`;

  // Error state
  if (error) {
    return (
      <div className="container py-5 text-center">
        <div className="alert alert-danger shadow-sm">
          <h3>Oops!</h3>
          <p>{error}</p>
          <Link to="/" className="btn btn-primary mt-3">Back to Knowledge Hub</Link>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="blog-loader">
          <div className="spinner-grow text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="spinner-grow text-secondary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="spinner-grow text-success" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <p className="mt-3">Loading blog post...</p>
      </div>
    );
  }

  // Not found state
  if (!blog) {
    return (
      <div className="container py-5 text-center">
        <div className="alert alert-warning shadow-sm">
          <h3>Blog post not found</h3>
          <p>The blog post you're looking for doesn't exist or has been removed.</p>
          <Link to="/" className="btn btn-primary mt-3">Back to Knowledge Hub</Link>
        </div>
      </div>
    );
  }

  // Determine badge color based on tag (if exists)
  const badgeColor = blog.blog_tags && blog.blog_tags.length > 0 ? 'warning' : 'info';
  const tagLabel = blog.blog_tags && blog.blog_tags.length > 0 ?
    (typeof blog.blog_tags[0] === 'string' ? blog.blog_tags[0] : 'Blog') : 'Blog';

  // Get the first paragraph as excerpt
  const getExcerpt = (htmlContent) => {
    if (!htmlContent) return '';

    // Extract first paragraph
    const match = htmlContent.match(/<p>(.*?)<\/p>/);
    if (match && match[1]) {
      // Strip HTML tags and return
      return match[1].replace(/<[^>]*>/g, '');
    }
    return '';
  };

  return (
    <div className="single-blog-page">
      <Helmet>
        <title>{blog.blog_name}</title>
        <meta name="description" content={blog.blog_description} />
        <meta name="keywords" content={blog.blog_keyword} />
        <meta property="og:title" content={blog.blog_slug} />
        <meta property="og:description" content={blog.blog_description} />
        <meta property="og:type" content="article" />
      </Helmet>

      {/* Reading progress indicator */}
      <div id="reading-progress-container" className="reading-progress-container">
        <div id="reading-progress" className="reading-progress"></div>
      </div>

      {/* Hero Section with Featured Image */}
      <header className="blog-hero position-relative">
        <div className="blog-hero-bg" style={{ backgroundImage: `url(${blog.blog_image ? `https://www.legalfintax.com/storage/blogimages/${blog.blog_image}` : null})` }}>
          <div className="overlay"></div>
        </div>
        <div className="container position-relative py-5">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center text-white">
              <span className={`badge bg-${badgeColor} mb-3 px-3 py-2`}>{tagLabel}</span>
              <h1 className="display-4 fw-bold mb-4">{blog.blog_name}</h1>
              <div className="blog-meta-sec text-dark">
                <span className="me-3">
                  <i className="bi bi-calendar me-2"></i>
                  {formatDate(blog.created_at)}
                </span>
                <span className="me-3">
                  <i className="bi bi-clock me-2"></i>
                  {readingTime}
                </span>
              </div>

              {/* Mobile scroll down indicator */}
              <div className="scroll-down-indicator d-md-none mt-5">
                <span>Scroll Down</span>
                <i className="bi bi-chevron-down"></i>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-5 bg-light">
        <div className="container">
          <div className="row">
            {/* Main Blog Content */}
            <div className="col-lg-8">
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-4 p-lg-5">
                  {/* Top sharing buttons visible on small screens */}
                  <div className="d-md-none text-center mb-4">
                    <div className="share-buttons-container">
                      <button onClick={(e) => handleShare('facebook', e)} className="btn btn-light share-btn">
                        <i className="bi bi-facebook"></i>
                      </button>
                      <button onClick={(e) => handleShare('twitter', e)} className="btn btn-light share-btn">
                        <i className="bi bi-twitter-x"></i>
                      </button>
                      <button onClick={(e) => handleShare('linkedin', e)} className="btn btn-light share-btn">
                        <i className="bi bi-linkedin"></i>
                      </button>
                      <button onClick={(e) => handleShare('whatsapp', e)} className="btn btn-light share-btn">
                        <i className="bi bi-whatsapp"></i>
                      </button>
                    </div>
                  </div>

                  {/* Description paragraph */}
                  <div className="lead mb-4 blog-description">{blog.blog_description}</div>

                  {/* Full Content */}
                  <div className="blog-content-sec">
                    {/* Introduction section with proper ID */}
                    <div id="introduction" ref={introductionRef} className="blog-section">
                      <div dangerouslySetInnerHTML={{ __html: blog && blog.blog_text }} />
                    </div>

                    {/* Keyword sections with properly formatted IDs */}
                    {hasTags && blogTags.length > 0 && blogTags.map((obj, index) => {
                      const sectionId = getSafeId(obj.blog_tag);
                      return (
                        <div key={index} id={sectionId} className="blog-section" ref={el => sectionRefs.current[sectionId] = el}>
                          <h2 className="mt-5 mb-4">{obj.blog_tag}</h2>
                          <div dangerouslySetInnerHTML={{ __html: obj.blog_description }} />
                        </div>
                      );
                    })}
                  </div>

                  {/* Social Sharing */}
                  <div className="text-center my-5 pt-4 border-top">
                    <h5 className="mb-3">Share This Article</h5>
                    <div className="d-flex justify-content-center gap-3">
                      <button
                        onClick={(e) => handleShare('facebook', e)}
                        className="btn share-btn-large"
                        style={{ backgroundColor: "#3b5998", color: "white" }}
                      >
                        <i className="bi bi-facebook me-2"></i>
                        Facebook
                      </button>

                      <button
                        onClick={(e) => handleShare('twitter', e)}
                        className="btn share-btn-large"
                        style={{ backgroundColor: "#000000", color: "white" }}
                      >
                        <i className="bi bi-twitter-x me-2"></i>
                        Twitter
                      </button>

                      <button
                        onClick={(e) => handleShare('linkedin', e)}
                        className="btn share-btn-large"
                        style={{ backgroundColor: "#0077b5", color: "white" }}
                      >
                        <i className="bi bi-linkedin me-2"></i>
                        LinkedIn
                      </button>

                      <a
                        href={emailShareUrl}
                        className="btn share-btn-large"
                        style={{ backgroundColor: "#6c757d", color: "white" }}
                      >
                        <i className="bi bi-envelope-fill me-2"></i>
                        Email
                      </a>
                    </div>
                  </div>

                  {/* FAQ Section */}
                  {hasFaqs && blogFaqs.length > 0 && (
                    <div className="faq-section mt-5" id="faqs" ref={faqRef}>
                      <h2 className="mb-4 text-center">Frequently Asked Questions</h2>
                      <div className="accordion" id="faqAccordion">
                        {blogFaqs.map((faq, index) => (
                          <div className="accordion-item" key={index}>
                            <h2 className="accordion-header" id={`heading${index}`}>
                              <button
                                className="accordion-button collapsed"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target={`#collapse${index}`}
                                aria-expanded="false"
                                aria-controls={`collapse${index}`}
                              >
                                <span dangerouslySetInnerHTML={{ __html: faq.blog_question }} />
                              </button>
                            </h2>
                            <div
                              id={`collapse${index}`}
                              className="accordion-collapse collapse"
                              aria-labelledby={`heading${index}`}
                              data-bs-parent="#faqAccordion"
                            >
                              <div className="accordion-body">
                                <div dangerouslySetInnerHTML={{ __html: faq.blog_answer }} />
                              </div>
                            </div>

                          </div>

                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <FaqStructuredData blogFaqData={blogFaqs} />

              </div>


            </div>

            {/* Sidebar */}
            <div className="col-lg-4">
              {/* Floating Share Buttons for Desktop */}
              <div className="d-none d-md-block">
                <div className="floating-share-buttons">
                  <button onClick={(e) => handleShare('facebook', e)} className="btn btn-light share-btn mb-2">
                    <i className="bi bi-facebook"></i>
                  </button>
                  <button onClick={(e) => handleShare('twitter', e)} className="btn btn-light share-btn mb-2">
                    <i className="bi bi-twitter-x"></i>
                  </button>
                  <button onClick={(e) => handleShare('linkedin', e)} className="btn btn-light share-btn mb-2">
                    <i className="bi bi-linkedin"></i>
                  </button>
                  <button onClick={(e) => handleShare('whatsapp', e)} className="btn btn-light share-btn mb-2">
                    <i className="bi bi-whatsapp"></i>
                  </button>
                  <a href={emailShareUrl} className="btn btn-light share-btn">
                    <i className="bi bi-envelope-fill"></i>
                  </a>
                </div>
              </div>

              {/* Table of Contents */}
              <div className="card shadow-sm border-0 mb-4 sticky-top" style={{ top: '2rem' }}>
                <div className="card-body p-4">
                  <h5 className="mb-3">Table of Contents</h5>
                  <nav className="toc">
                    <ul className="nav flex-column">
                      <li className="nav-item">
                        <a
                          className={`nav-link ${activeSection === 'introduction' ? 'active' : ''}`}
                          href="#introduction"
                          onClick={(e) => {
                            e.preventDefault();
                            scrollToSection('introduction');
                          }}
                        >
                          <i className="bi bi-chevron-right me-2"></i>
                          Introduction
                        </a>
                      </li>

                      {/* Display keywords in the table of contents */}
                      {blogTags && blogTags.map((obj, index) => {
                        const sectionId = getSafeId(obj.blog_tag);
                        return (
                          <li className="nav-item" key={`toc-${index}`}>
                            <a
                              className={`nav-link ${activeSection === sectionId ? 'active' : ''}`}
                              href={`#${sectionId}`}
                              onClick={(e) => {
                                e.preventDefault();
                                scrollToSection(sectionId);
                              }}
                            >
                              <i className="bi bi-chevron-right me-2"></i>
                              {obj.blog_tag}
                            </a>
                          </li>
                        );
                      })}

                      {hasFaqs && blogFaqs.length > 0 && (
                        <li className="nav-item">
                          <a
                            className={`nav-link ${activeSection === 'faqs' ? 'active' : ''}`}
                            href="#faqs"
                            onClick={(e) => {
                              e.preventDefault();
                              scrollToSection('faqs');
                            }}
                          >
                            <i className="bi bi-chevron-right me-2"></i>
                            FAQs
                          </a>
                        </li>
                      )}
                    </ul>
                  </nav>
                </div>
              </div>


              {/* Related Posts */}
              <div className="card shadow-sm border-0">
                <div className="card-body p-4">
                  <h5 className="mb-3">Related Posts</h5>
                  {relatedPosts.length > 0 ? (
                    relatedPosts.map((post) => (
                      <div key={post.blog_id} className="mb-3 pb-3 border-bottom related-post">
                        <div className="row g-0">
                          <div className="col-3">
                            <img
                              src={post.blog_image ? `https://www.legalfintax.com/storage/blogimages/${post.blog_image}` : null}
                              alt={post.blog_name}
                              className="img-fluid rounded"
                              width="70"
                              height="70"
                              style={{ objectFit: 'cover' }}
                            />
                          </div>
                          <div className="col-9">
                            <div className="ps-3">
                              {post.blog_tags && post.blog_tags.length > 0 && (
                                <span className="badge bg-warning bg-opacity-10 text-warning mb-1">
                                  {typeof post.blog_tags === 'string' ?
                                    JSON.parse(post.blog_tags)[0] :
                                    post.blog_tags[0]}
                                </span>
                              )}
                              <h6 className="mb-1">
                                <Link to={`/knowledge/${post.blog_slug}`} className="text-decoration-none stretched-link">
                                  {post.blog_name}
                                </Link>
                              </h6>
                              <small className="text-muted d-block mb-1">{formatDate(post.created_at)}</small>
                              <small className="text-muted">{calculateReadingTime(post.blog_text)}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted">No related posts found.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-dark text-white pt-5 pb-4">
        <div className="container">
          <div className="row g-4">
            {/* Company Info */}
            <div className="col-lg-4 mb-4">
              <div className="d-flex align-items-center mb-3">
                <img src="/logo.png" height={50} width={50} alt="LegalFinTax Logo" className="me-2" />
                <h2 className="m-0 fw-bold">LegalFinTax</h2>
              </div>

              <p className="mb-3 footer-about">
                Providing expert financial and tax solutions to simplify your financial journey.
                Trust our expertise for all your taxation and compliance needs.
              </p>

              <div className="social-icons mt-3">
                <a href="https://www.facebook.com/legalfintax" className="social-icon" aria-label="Facebook">
                  <i className="bi bi-facebook"></i>
                </a>
                <a href="https://twitter.com" className="social-icon" aria-label="Twitter">
                  <i className="bi bi-twitter"></i>
                </a>
                <a href="https://www.linkedin.com/company/legalfintax" className="social-icon" aria-label="LinkedIn">
                  <i className="bi bi-linkedin"></i>
                </a>
                <a href="https://www.instagram.com/legal.fin.tax/" className="social-icon" aria-label="Instagram">
                  <i className="bi bi-instagram"></i>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className="col-lg-2 col-md-4 mb-4">
              <h5 className="footer-heading">Quick Links</h5>
              <ul className="footer-links">
                <li><a href="https://legalfintax.com/">Home</a></li>
                <li><a href="https://legalfintax.com/contact-us">Contact</a></li>
                <li><a href="https://rupela.in/">Accounting Software</a></li>
              </ul>
            </div>

            {/* Services */}
            <div className="col-lg-3 col-md-4 mb-4">
              <h5 className="footer-heading">Our Services</h5>
              <ul className="footer-links">
                <li><a href="https://www.legalfintax.com/income-tax-return">Income Tax Return</a></li>
                <li><a href="https://www.legalfintax.com/gst-registration">GST Registration</a></li>
                <li><a href="https://www.legalfintax.com/trademark-registration">Trademark Registration</a></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="col-lg-3 col-md-4 mb-4">
              <h5 className="footer-heading">Contact Us</h5>
              <ul className="footer-links contact-info">
                <li>
                  <i className="bi bi-geo-alt-fill me-2"></i>
                  A-71, Karni Nagar, Pawanpuri<br />Bikaner, Rajasthan (334003), India
                </li>
                <li>
                  <i className="bi bi-telephone-fill me-2"></i>
                  <a href="tel:+919079398040">+91 9079398040</a>
                </li>
                <li>
                  <i className="bi bi-envelope-fill me-2"></i>
                  <a href="mailto:info@legalfintax.com">info@legalfintax.com</a>
                </li>

              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="row mt-4 pt-3 border-top border-secondary">
            <div className="col-md-6 text-center text-md-start">
              <p className="small mb-0">© {new Date().getFullYear()} LegalFinTax. All rights reserved.</p>
            </div>
            <div className="col-md-6 text-center text-md-end">
              <a href="https://www.legalfintax.com/privacy-policy" className="footer-policy-link">Privacy Policy</a>
              <a href="https://www.legalfintax.com/terms-and-conditions" className="footer-policy-link">Terms of Service</a>
              <a href="https://www.legalfintax.com/refund-policy" className="footer-policy-link">Refund Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Blog;